// Detects when the currently-loaded JS bundle is stale relative to what is
// actually deployed right now, and forces exactly one reload to recover.
//
// index.html is already served with Cache-Control: no-cache, no-store,
// must-revalidate (see deploy-staging.yml's Front Door purge step + post-deploy
// verification), so under normal conditions a browser can never load an old
// index.html paired with an old entry-chunk hash. This guard exists for the
// abnormal case: a browser bug, an aggressive corporate/ISP proxy, or the
// back/forward cache restoring a page without re-validating despite those
// headers. In that scenario the WHOLE page (index.html + entry chunk) is a
// self-consistent OLD snapshot -- nothing 404s, nothing throws, so the
// lazy-chunk-404 guard in GlobalErrorBoundary.tsx's RouteErrorFallback (which
// only fires when a chunk reference is BROKEN, e.g. new shell + old chunk
// hash) never triggers. This is the failure mode behind "I still see the old
// login screen sometimes" (2026-08-11) -- diagnosed as a stale bundle, this
// closes the gap structurally instead of relying on browsers behaving.
//
// Method: fetch a genuinely fresh index.html (cache: "no-store", belt-and-
// suspenders on top of the server headers) and compare its entry <script> src
// against the one actually loaded in this document. A mismatch means this
// page load itself is stale -- reload once. Guarded by sessionStorage so a
// persistent mismatch (e.g. this very check somehow hitting a cached response)
// can never loop; one attempt per session per short window is the entire
// remedy, looping would be worse than the stale page.

const RELOAD_GUARD_KEY = "velaro.staleBundleReloadAttemptedAt";
const RELOAD_GUARD_WINDOW_MS = 60_000;

function getLoadedEntrySrc(): string | null {
  const script = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src]'
  );
  return script?.getAttribute("src") ?? null;
}

function extractEntrySrc(html: string): string | null {
  const match = html.match(/<script[^>]*type="module"[^>]*\ssrc="([^"]+)"/i);
  return match?.[1] ?? null;
}

export async function checkForStaleBundle(): Promise<void> {
  try {
    const loadedSrc = getLoadedEntrySrc();
    // No module script found (unexpected markup, or a dev-server shape we
    // don't recognize) -- nothing to safely compare against, don't guess.
    if (!loadedSrc) return;

    const lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - lastAttempt < RELOAD_GUARD_WINDOW_MS) return;

    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res.ok) return;
    const html = await res.text();
    const freshSrc = extractEntrySrc(html);
    if (!freshSrc || freshSrc === loadedSrc) return;

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    console.warn(
      "[StaleBundleGuard] Loaded bundle does not match the live deploy — forcing a one-time reload",
      { loadedSrc, freshSrc }
    );
    window.location.reload();
  } catch {
    // Never let this check itself break the app — if freshness can't be
    // confirmed (offline, network blip), silently leave the page as-is.
  }
}
